/* Types + presentation constants for `enable_radius_scoring`.
   Shape verified against a live flag-on run of the external vitamin API
   (POST /api/concept-test-vitamin) — see specs/26-vitamin-concept-test-flags/spec-02.md. */

export type RadiusLevel = 0 | 1 | 2 | 3;

export interface RadiusSentence {
  text: string;
  radius: RadiusLevel;
  /** A quoted consumer verbatim at level 0, a description at 1-2, and null at level 3. */
  backing?: string | null;
  /** Only ever "novel_insight", and only on level-3 sentences. */
  tag?: string | null;
}

export interface RadiusItem {
  radius: RadiusLevel;
  sentences: RadiusSentence[];
}

export interface RadiusThresholds {
  sim_grounded?: number;
  sim_inferred?: number;
  min_aggregate?: number;
  numeric_tolerance_pp?: number;
}

export interface RadiusBlock {
  method?: string;
  scale?: Record<string, string>;
  thresholds?: RadiusThresholds;
  embeddings_available?: boolean;
  corpus_size?: number;
  corpus_consumers?: number;
  sentences_scored?: number;
  llm_adjudicated?: number;
  distribution?: Record<string, number>;
  novel_count?: number;
  failed_numeric_claims?: unknown[];
  /** Keyed by content-addressed path: "verdict", "insight.<headline>", "frictions.<name>", … */
  items?: Record<string, RadiusItem>;
}

export interface RadiusLevelMeta {
  level: RadiusLevel;
  /** The API's own scale name, for reference against the raw payload. */
  key: string;
  label: string;
  color: string;
  /** Lighter variant, for marks sitting on the dark hero panel. */
  colorOnDark: string;
}

export const RADIUS_LEVELS: readonly RadiusLevelMeta[] = [
  { level: 0, key: 'grounded', label: 'Grounded in a consumer verbatim', color: '#15803d', colorOnDark: '#4ade80' },
  { level: 1, key: 'inferred', label: 'Inferred from a shared pattern', color: '#1d4ed8', colorOnDark: '#93c5fd' },
  { level: 2, key: 'logically_derived', label: 'Derived from study figures', color: '#b45309', colorOnDark: '#fcd34d' },
  { level: 3, key: 'novel', label: 'No source in the corpus', color: '#dc2626', colorOnDark: '#fca5a5' },
];

export const RADIUS_LEVEL_COUNT = RADIUS_LEVELS.length;

const DEFAULT_SIM_GROUNDED = 0.72;
const DEFAULT_MIN_AGGREGATE = 3;

/** The one-line explanation of how a level is decided, using the run's own thresholds. */
export const radiusMethodFor = (level: RadiusLevel, thresholds?: RadiusThresholds): string => {
  switch (level) {
    case 0:
      return `Similarity to a twin's own words ≥ ${thresholds?.sim_grounded ?? DEFAULT_SIM_GROUNDED}`;
    case 1:
      return `Aggregated across ${thresholds?.min_aggregate ?? DEFAULT_MIN_AGGREGATE} or more twins`;
    case 2:
      return 'Built from study figures rather than speech';
    default:
      return 'Written by the analyst — nothing in the corpus supports it';
  }
};

export const radiusLevelMeta = (level: RadiusLevel): RadiusLevelMeta =>
  RADIUS_LEVELS[level] ?? RADIUS_LEVELS[RADIUS_LEVELS.length - 1];
